import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';

const MENU = [
  {
    label: 'Products',
    to: '/products',
    columns: [
      { title: 'Collection', links: [
        ['Collections', '/collections'],
        ['All Products', '/products'],
        ['Capsule Suites', '/products?family=capsule'],
        ['Studio Suites', '/products?family=studio'],
      ]},
      { title: 'Signature Models', links: [
        ['CS37', '/products/cs37'],
        ['CS33', '/products/cs33'],
        ['CS31', '/products/cs31'],
        ['SS35', '/products/ss35'],
      ]},
    ],
  },
  {
    label: 'Applications',
    to: '/applications',
    columns: [
      { title: 'Hospitality', links: [
        ['Overview', '/hospitality'],
        ['Luxury Resorts', '/hospitality#resorts'],
        ['Boutique Hotels', '/hospitality#hotels'],
        ['Glamping', '/hospitality#glamping'],
        ['Airbnb & Rentals', '/hospitality#rentals'],
        ['Investment Estimator', '/hospitality#estimator'],
      ]},
      { title: 'Residential', links: [
        ['Overview', '/residential'],
        ['Weekend Homes', '/residential#weekend'],
        ['Farmhouses', '/residential#farmhouses'],
      ]},
      { title: 'Commercial Spaces', links: [
        ['Overview', '/commercial'],
        ['Retail & Pop-ups', '/commercial#retail'],
        ['Office Spaces', '/commercial#office'],
      ]},
    ],
  },
  {
    label: 'Technology',
    to: '/technology',
    columns: [
      { title: 'How it is made', links: [
        ['Technology', '/technology'],
        ['Engineering', '/engineering'],
        ['Materials & Finishes', '/materials'],
      ]},
      { title: 'Quality & Standards', links: [
        ['Precision & Tolerance', '/engineering#precision'],
        ['Structural Integrity', '/engineering#structural'],
        ['Warranty & Roadmap', '/engineering#warranty'],
      ]},
    ],
  },
  {
    label: 'Journal',
    to: '/journal',
    single: true,
  },
  {
    label: 'Company',
    to: '/about',
    columns: [
      { title: 'The Studio', links: [
        ['About', '/about'],
        ['Gallery', '/gallery'],
        ['Journal', '/journal'],
      ]},
      { title: 'Support', links: [
        ['FAQ', '/faq'],
        ['Contact', '/contact'],
        ['Book Consultation', '/contact#book'],
      ]},
    ],
  },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState(null);
  const location = useLocation();
  const forceLight = location.pathname !== '/';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setOpen(false); setHovered(null); }, [location.pathname]);

  const isLight = scrolled || forceLight || hovered !== null;
  const hoveredMenu = MENU.find((m) => m.label === hovered);

  return (
    <header
      data-testid="site-navbar"
      onMouseLeave={() => setHovered(null)}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        isLight ? 'bg-white/95 backdrop-blur-xl border-b border-black/5 text-[#111]' : 'bg-transparent text-white'
      }`}
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 flex items-center justify-between h-16 md:h-20">
        <Link to="/" data-testid="nav-logo" className="font-display text-lg md:text-xl tracking-[0.28em]">
          MODULAROOM
        </Link>
        <nav className="hidden lg:flex items-center gap-8">
          {MENU.map((m) => (
            <div key={m.label} onMouseEnter={() => setHovered(m.single ? null : m.label)} className="relative">
              <Link
                to={m.to}
                data-testid={`nav-link-${m.label.toLowerCase().replace(/[^a-z]/g, '-')}`}
                className="inline-flex items-center gap-1 text-[13px] tracking-[0.18em] uppercase transition-opacity hover:opacity-60"
              >
                {m.label} {!m.single && <ChevronDown size={12} className="opacity-60" />}
              </Link>
            </div>
          ))}
          <Link
            to="/contact#book"
            data-testid="nav-book-consultation"
            className={`ml-2 rounded-full px-5 py-2.5 text-[12px] tracking-[0.2em] uppercase transition-colors ${
              isLight ? 'bg-[#111] text-white hover:bg-[#C7A36A]' : 'bg-white text-[#111] hover:bg-[#C7A36A] hover:text-white'
            }`}
          >
            Book Consultation
          </Link>
        </nav>
        <button
          data-testid="nav-menu-toggle"
          aria-label="Open menu"
          className="lg:hidden p-2 -mr-2"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {hoveredMenu && hoveredMenu.columns && (
        <div className="hidden lg:block bg-white text-[#111] border-t border-black/5" data-testid={`megamenu-${hovered.toLowerCase().replace(/[^a-z]/g, '-')}`}>
          <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-12 grid grid-cols-6 gap-10">
            {hoveredMenu.columns.map((c) => (
              <div key={c.title} className="col-span-1">
                <div className="text-[10px] tracking-[0.32em] uppercase text-black/40 mb-5">{c.title}</div>
                <ul className="space-y-3">
                  {c.links.map(([label, to]) => (
                    <li key={label}>
                      <Link to={to} className="text-[15px] hover:text-[#C7A36A] transition-colors">{label}</Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
            <div className={`border-l border-black/10 pl-10`} style={{ gridColumn: `span ${6 - hoveredMenu.columns.length} / span ${6 - hoveredMenu.columns.length}` }}>
              <div className="text-[10px] tracking-[0.32em] uppercase text-[#C7A36A] mb-4">Speak with a specialist</div>
              <div className="font-display text-3xl leading-tight tracking-tight">Every project begins with a conversation.</div>
              <Link to="/contact#book" className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#111] text-white px-6 py-3 text-[12px] tracking-[0.2em] uppercase hover:bg-[#C7A36A] transition-colors">
                Book Consultation
              </Link>
            </div>
          </div>
        </div>
      )}

      {open && (
        <div className="lg:hidden bg-white text-[#111] border-t border-black/5" data-testid="mobile-menu">
          <div className="px-6 py-6 flex flex-col gap-6 max-h-[80vh] overflow-y-auto">
            {MENU.map((m) => (
              <div key={m.label}>
                <div className="text-[10px] tracking-[0.28em] uppercase text-black/40 mb-3">{m.label}</div>
                <div className="space-y-2">
                  {m.single ? (
                    <Link to={m.to} className="block text-base">{m.label}</Link>
                  ) : (
                    m.columns.flatMap((c) => c.links).map(([label, to]) => (
                      <Link key={label} to={to} className="block text-base">{label}</Link>
                    ))
                  )}
                </div>
              </div>
            ))}
            <Link to="/contact#book" className="mt-4 rounded-full bg-[#111] text-white text-center py-3 tracking-[0.2em] uppercase text-xs">
              Book Consultation
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
